import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";
import { Link } from "react-router-dom";

import { cn } from "@/lib/utils"
import { useAxiosContext } from "@context/axiosinstance";

const buttonVariants = cva(
  "inline-flex items-center font-sans justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-button-primary text-[#fff] shadow hover:bg-primary",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-primary-foreground shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        text:'bg-transparent shadow-none text-base text-primary',
        rounded:'!rounded-full flex items-center justify-center bg-transparent shadow-none !h-auto !w-auto !p-0',
        successOutline: "border border-green-500 text-green-600 bg-white hover:bg-green-50 disabled:border-green-500 disabled:text-green-400 disabled:bg-white",

      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

/**
 * @typedef {React.ButtonHTMLAttributes<HTMLButtonElement> & React.AnchorHTMLAttributes<HTMLAnchorElement>} BaseProps
 */

/**
 * @typedef {Object} VariantProps
 * @property {"default"|"destructive"|"outline"|"ghost"|"secondary"|"link"|"successOutline"} [variant]
 * @property {"default"|"sm"|"lg"|"icon"} [size]
 * @property {boolean} [asChild]
 * @property {"button"|"link"|"anchor"} [as] - How to render: button (default), react-router Link, or <a>.
 * @property {string} [href] - Destination (required for as="link" or "anchor").
 * @property {React.ReactNode} [startIcon] - Optional icon element before the text.
 */

/**
 * @typedef {BaseProps & VariantProps} ButtonProps
 */

/**
 * @type {React.ForwardRefExoticComponent<ButtonProps & React.RefAttributes<HTMLButtonElement | HTMLAnchorElement>>}
 */
const Button = React.forwardRef(
  (
    /** @type {ButtonProps} */
    { className, variant = "default", size = "default", asChild = false, as = "button", href, startIcon, children,disabled, ...props },
    ref
  ) => {
       const { isButtonDisabled } = useAxiosContext();
        const disableBtn = isButtonDisabled || disabled;

    let Comp = "button";

    if (asChild) {
      Comp = Slot;
    } else if (as === "link" && href) {
      Comp = Link;
    } else if (as === "anchor" && href) {
      Comp = "a";
    }

    return (
      <Comp
        ref={ref}
        to={as === "link" ? href : undefined}
        href={as === "anchor" ? href : undefined}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disableBtn}
        {...props}
      >
        {startIcon && <span className="mr-2">{startIcon}</span>}
        {children}
      </Comp>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants }
